import { ScreenHeader } from "@/components/common/ScreenHeader";
import { ReferralQr } from "@/components/referral/ReferralQr";
import { ThemedText } from "@/components/themed-text";
import { registerUrl } from "@/constants";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { showAlert } from "@/lib/alert";
import { getApiErrorMessage } from "@/lib/api";
import referralService from "@/lib/services/referrals";
import { showToast } from "@/lib/toast";
import { money } from "@/lib/utils";
import FontAwesome6 from "@expo/vector-icons/FontAwesome6";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQuery } from "@tanstack/react-query";
import * as Clipboard from "expo-clipboard";
import React, { useState } from "react";
import {
  Linking,
  Modal,
  Platform,
  ScrollView,
  Share,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function ReferralScreen() {
  const { user, theme } = useAuth();
  const referralCode = user?.referral_code || "N/A";
  const registerLink = `${registerUrl}?ref=${referralCode}`;
  const [appealReason, setAppealReason] = useState("");
  const [shareVisible, setShareVisible] = useState(false);
  const { data: summary } = useQuery({
    queryKey: ["referrals"],
    queryFn: () => referralService.summary(),
  });
  const shareMessage = `Join me on Auto Transporter UK! Use my referral code: ${referralCode} or register directly here: ${registerLink}`;
  const stats = [
    { label: "Invited", value: summary?.invited_count ?? 0 },
    { label: "Joined", value: summary?.joined_count ?? 0 },
    { label: "Completed", value: summary?.completed_count ?? 0 },
  ];
  const socialOptions = [
    {
      label: "WhatsApp",
      icon: "logo-whatsapp" as const,
      url: `https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
    },
    {
      label: "Facebook",
      icon: "logo-facebook" as const,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(registerLink)}`,
    },
    {
      label: "X",
      icon: "logo-twitter" as const,
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareMessage)}`,
    },
    {
      label: "Email",
      icon: "mail-outline" as const,
      url: `mailto:?subject=${encodeURIComponent("Join me on Auto Transporter UK")}&body=${encodeURIComponent(shareMessage)}`,
    },
  ];

  const handleCopy = async () => {
    try {
      await Clipboard.setStringAsync(referralCode);
      showAlert("Copied!", "Referral code copied to clipboard.");
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  const handleShare = async () => {
    try {
      await Share.share({ message: shareMessage });
    } catch (error: any) {
      showAlert("Error", error.message);
    }
  };

  const handleSocialShare = async (url: string) => {
    try {
      if (Platform.OS === "web") {
        window.open(url, "_blank", "noopener,noreferrer");
        return;
      }
      await Linking.openURL(url);
    } catch {
      showToast.error("That app could not be opened.");
    }
  };

  const handleCopyLink = async () => {
    await Clipboard.setStringAsync(registerLink);
    showAlert("Copied!", "Referral link copied to clipboard.");
  };

  const handleAppeal = async (referralId: number) => {
    if (!appealReason.trim()) {
      showToast.error("Tell us why this referral should be reviewed.");
      return;
    }

    try {
      await referralService.appeal(referralId, appealReason.trim());
      setAppealReason("");
      showToast.success("Appeal submitted.");
    } catch (error: unknown) {
      showToast.error(
        getApiErrorMessage(error, "The appeal could not be submitted."),
      );
    }
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top", "bottom"]}
    >
      <ScreenHeader title="Invite a Friend" />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentInner}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.illustrationContainer}>
          <Ionicons
            name="person-add-outline"
            size={28}
            color={BrandColors.primary}
          />
        </View>

        <ThemedText style={styles.title} type="subtitle">
          Invite a Friend
        </ThemedText>
        <ThemedText style={styles.description} type="extraSmall">
          Invite friends to join and earn wallet credit when they complete their
          first booking.
        </ThemedText>

        <View
          style={[
            styles.card,
            { backgroundColor: theme.card, borderColor: theme.border },
          ]}
        >
          <View style={styles.pointsRow}>
            <ThemedText style={styles.statLabel} type="extraSmall">
              Wallet balance
            </ThemedText>
            <ThemedText style={styles.pointsValue} type="defaultSemiBold">
              {money(
                Number(summary?.wallet_balance ?? user?.wallet_balance ?? 0),
              )}
            </ThemedText>
          </View>
          <View style={[styles.statsRow, { borderTopColor: theme.border }]}>
            {stats.map((stat, index) => (
              <View
                key={stat.label}
                style={[
                  styles.statItem,
                  index > 0 && {
                    borderLeftWidth: StyleSheet.hairlineWidth,
                    borderLeftColor: theme.border,
                  },
                ]}
              >
                <ThemedText style={styles.statValue} type="defaultSemiBold">
                  {stat.value}
                </ThemedText>
                <ThemedText style={styles.statLabel} type="extraSmall">
                  {stat.label}
                </ThemedText>
              </View>
            ))}
          </View>
        </View>

        <View
          style={[
            styles.card,
            { backgroundColor: theme.card, borderColor: theme.border },
          ]}
        >
          <ThemedText style={styles.codeLabel} type="extraSmall">
            Referral code
          </ThemedText>
          <View style={styles.codeField}>
            <ThemedText style={styles.codeText} type="defaultSemiBold">
              {referralCode}
            </ThemedText>
            <TouchableOpacity
              onPress={handleCopy}
              style={styles.copyButton}
              accessibilityLabel="Copy referral code"
            >
              <Ionicons
                name="copy-outline"
                size={18}
                color={BrandColors.primary}
              />
            </TouchableOpacity>
          </View>
        </View>

        <TouchableOpacity
          style={styles.shareButton}
          onPress={() => setShareVisible(true)}
        >
          <Ionicons
            name="share-social-outline"
            size={22}
            color="white"
          />
          <ThemedText style={styles.shareButtonText} type="defaultSemiBold">
            Share with friends
          </ThemedText>
        </TouchableOpacity>

        <View style={styles.balanceRow}>
          <ThemedText type="extraSmall">
            Wallet{" "}
            {money(
              Number(summary?.wallet_balance ?? user?.wallet_balance ?? 0),
            )}
          </ThemedText>
          <ThemedText type="extraSmall">
            Pending {money(Number(summary?.pending_amount_gbp ?? 0))}
          </ThemedText>
        </View>

        {summary?.referrals?.map((referral) => (
          <View key={referral.id} style={styles.referralRow}>
            <ThemedText type="defaultSemiBold">
              {referral.status_label}
            </ThemedText>
            <ThemedText type="extraSmall">{referral.explanation}</ThemedText>
            {referral.can_appeal && (
              <>
                <TextInput
                  value={appealReason}
                  onChangeText={setAppealReason}
                  placeholder="Why should this be reviewed?"
                  placeholderTextColor={BrandColors.mediumGray}
                  style={[styles.convertInput, { color: theme.text }]}
                />
                <TouchableOpacity onPress={() => handleAppeal(referral.id)}>
                  <ThemedText
                    type="defaultSemiBold"
                    style={{ color: BrandColors.primary }}
                  >
                    Submit appeal
                  </ThemedText>
                </TouchableOpacity>
              </>
            )}
          </View>
        ))}

        <View style={styles.infoBox}>
          <Ionicons
            name="information-circle-outline"
            size={20}
            color={BrandColors.mediumGray}
          />
          <ThemedText style={styles.infoText} type="extraSmall">
            {summary?.terms ||
              "Your friend will get a bonus on their first booking, and you'll receive credit in your wallet. By sharing your code you agree that we record the referral when they sign up."}
          </ThemedText>
        </View>
      </ScrollView>

      <Modal
        visible={shareVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setShareVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              { backgroundColor: theme.background, borderColor: theme.border },
            ]}
          >
            <View style={styles.modalHeader}>
              <ThemedText type="defaultSemiBold">Share with friends</ThemedText>
              <TouchableOpacity
                onPress={() => setShareVisible(false)}
                accessibilityLabel="Close"
              >
                <Ionicons name="close" size={22} color={theme.text} />
              </TouchableOpacity>
            </View>

            {referralCode !== "N/A" && (
              <View style={styles.qrBlock}>
                <ThemedText style={styles.statLabel} type="extraSmall">
                  Scan the QR code to sign up
                </ThemedText>
                <View style={styles.qrFrame}>
                  <ReferralQr value={registerLink} />
                </View>
                <ThemedText style={styles.linkText} type="extraSmall">
                  {registerLink}
                </ThemedText>
                <TouchableOpacity onPress={handleCopyLink}>
                  <ThemedText
                    type="defaultSemiBold"
                    style={{ color: BrandColors.primary }}
                  >
                    Copy link
                  </ThemedText>
                </TouchableOpacity>
              </View>
            )}

            <ThemedText style={styles.statLabel} type="extraSmall">
              Or share on
            </ThemedText>
            <View style={styles.socialRow}>
              {socialOptions.map((option) => (
                <TouchableOpacity
                  key={option.label}
                  style={styles.socialItem}
                  onPress={() => handleSocialShare(option.url)}
                >
                  <View
                    style={[
                      styles.socialIcon,
                      {
                        backgroundColor: theme.card,
                        borderColor: theme.border,
                      },
                    ]}
                  >
                    {option.label == "X" ? (
                      <FontAwesome6
                        name="x-twitter"
                        size={24}
                        color={BrandColors.primary}
                      />
                    ) : (
                      <Ionicons
                        name={option.icon}
                        size={22}
                        color={BrandColors.primary}
                      />
                    )}
                  </View>
                  <ThemedText type="extraSmall">{option.label}</ThemedText>
                </TouchableOpacity>
              ))}
              <TouchableOpacity style={styles.socialItem} onPress={handleShare}>
                <View
                  style={[
                    styles.socialIcon,
                    { backgroundColor: theme.card, borderColor: theme.border },
                  ]}
                >
                  <Ionicons
                    name="share-social-outline"
                    size={22}
                    color={BrandColors.primary}
                  />
                </View>
                <ThemedText type="extraSmall">More</ThemedText>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  contentInner: {
    width: "100%",
    maxWidth: 400,
    alignSelf: "center",
  },
  illustrationContainer: {
    marginBottom: 20,
    alignItems: "center",
  },
  title: {
    marginBottom: 8,
    textAlign: "center",
  },
  description: {
    color: BrandColors.mediumGray,
    textAlign: "center",
    lineHeight: 18,
    marginBottom: 24,
    paddingHorizontal: 24,
  },
  card: {
    width: "100%",
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 16,
    marginBottom: 12,
  },
  pointsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: 14,
  },
  pointsValue: {
    fontSize: 22,
    lineHeight: 28,
    color: BrandColors.primary,
  },
  statsRow: {
    flexDirection: "row",
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 14,
  },
  statItem: {
    flex: 1,
    alignItems: "center",
  },
  codeLabel: {
    color: BrandColors.mediumGray,
    marginBottom: 8,
  },
  codeField: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderColor: BrandColors.primary + "80",
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  codeText: {
    fontFamily: Platform.select({ ios: "Menlo", default: "monospace" }),
    fontSize: 16,
    letterSpacing: 2,
  },
  copyButton: {
    padding: 4,
  },
  shareButton: {
    backgroundColor: BrandColors.primary,
    width: "100%",
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    borderRadius: 8,
    marginBottom: 32,
  },
  shareButtonText: {
    fontSize: 14,
    color: "white",
  },
  infoBox: {
    flexDirection: "row",
    padding: 16,
    borderRadius: 12,
    gap: 12,
    alignItems: "center",
    marginBottom: 24,
  },
  infoText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  qrBlock: {
    alignItems: "center",
    gap: 12,
  },
  qrFrame: {
    borderRadius: 8,
    overflow: "hidden",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalContent: {
    width: "100%",
    maxWidth: 380,
    borderRadius: 16,
    borderWidth: StyleSheet.hairlineWidth,
    padding: 20,
    gap: 16,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  socialRow: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  socialItem: {
    alignItems: "center",
    gap: 6,
  },
  socialIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    justifyContent: "center",
  },
  linkText: {
    textAlign: "center",
  },
  balanceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  convertInput: {
    borderWidth: 1,
    borderColor: BrandColors.mediumGray,
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  referralRow: {
    gap: 8,
    marginBottom: 16,
  },
  statValue: {
    fontSize: 18,
    lineHeight: 24,
  },
  statLabel: {
    fontSize: 12,
    color: BrandColors.mediumGray,
    marginTop: 2,
  },
});
